/**
 * RolesTable — V2 canonical table for role definitions, mirrors Seats:
 * Mode A (client-side) + ColumnDef + per-column filter row + multi-select
 * dropdown filters where it makes sense + Copy/CSV/Refresh/Columns toolbar +
 * row click → SlidePanel via parent's onRowClick.
 */
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  Copy, Download, Settings2, RefreshCw, FilterX,
  ArrowUp, ArrowDown, ArrowUpDown, ChevronRight, Users as UsersIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { useQueryClient } from '@tanstack/react-query';
import { useRoleDefinitions, roleDefinitionKeys } from '../api/roleDefinitions';
import type { RoleDefinition } from '../types';

interface ColumnDef {
  key: string;
  label: string;
  sortKey?: string;
  filterType?: 'text' | 'select';
  filterOptions?: { label: string; value: string }[];
  getValue: (r: RoleDefinition) => string | number;
  filterValues?: (r: RoleDefinition) => string[];
  render?: (r: RoleDefinition) => React.ReactNode;
  align?: 'left' | 'right';
  noFilter?: boolean;
}

const VISIBILITY_KEY = 'atek-roles-column-visibility';
const FILTERS_KEY = 'atek-roles-column-filters';

interface Props {
  onRowClick: (role: RoleDefinition) => void;
}

export function RolesTable({ onRowClick }: Props) {
  const qc = useQueryClient();
  const { data, isLoading, isFetching } = useRoleDefinitions();
  const roles = data?.roles ?? [];

  // Capability options (union across all roles)
  const capabilityOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of roles) for (const c of r.capabilities) set.add(c.label);
    return Array.from(set).sort().map((l) => ({ label: l, value: l }));
  }, [roles]);

  const accessOptions = useMemo(() => {
    const set = new Set<string>();
    for (const r of roles) for (const c of r.capabilities) set.add(c.access);
    return Array.from(set).sort().map((a) => ({ label: a, value: a }));
  }, [roles]);

  const COLUMNS: ColumnDef[] = useMemo(() => [
    { key: 'label', label: 'Role', sortKey: 'label', filterType: 'text',
      getValue: (r) => r.label,
      render: (r) => (
        <div className="min-w-0">
          <div className="font-medium truncate">{r.label}</div>
          <div className="text-[11px] font-mono text-muted-foreground truncate">{r.role}</div>
        </div>
      ) },
    { key: 'description', label: 'Description', filterType: 'text',
      getValue: (r) => r.description ?? '',
      render: (r) => r.description
        ? <span className="text-sm text-muted-foreground line-clamp-2">{r.description}</span>
        : <span className="text-xs text-muted-foreground italic">—</span> },
    { key: 'capabilities', label: 'Capabilities',
      filterType: 'select', filterOptions: capabilityOptions,
      getValue: (r) => r.capabilities.map((c) => c.label).join(', '),
      filterValues: (r) => r.capabilities.map((c) => c.label),
      render: (r) => {
        if (r.capabilities.length === 0) {
          return <span className="text-xs text-muted-foreground italic">none</span>;
        }
        return (
          <div className="flex flex-wrap gap-1">
            {r.capabilities.slice(0, 3).map((c, i) => (
              <Badge key={i} variant="secondary" className={cn(
                'text-[10px]',
                c.access === 'read-write' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800',
              )}>
                {c.label}
              </Badge>
            ))}
            {r.capabilities.length > 3 && (
              <Badge variant="outline" className="text-[10px] bg-muted">
                +{r.capabilities.length - 3}
              </Badge>
            )}
          </div>
        );
      } },
    { key: 'access', label: 'Access',
      filterType: 'select', filterOptions: accessOptions,
      getValue: (r) => Array.from(new Set(r.capabilities.map((c) => c.access))).join(', '),
      filterValues: (r) => Array.from(new Set(r.capabilities.map((c) => c.access))),
      render: (r) => {
        const types = Array.from(new Set(r.capabilities.map((c) => c.access)));
        if (types.length === 0) return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <div className="flex gap-1">
            {types.map((a) => (
              <Badge key={a} variant="secondary" className={cn(
                'text-[10px]',
                a === 'read-write' ? 'bg-emerald-100 text-emerald-800' : 'bg-blue-100 text-blue-800',
              )}>
                {a}
              </Badge>
            ))}
          </div>
        );
      } },
    { key: 'members', label: 'Members', sortKey: 'members',
      getValue: (r) => r.member_count ?? 0,
      align: 'right',
      render: (r) => (
        <span className="inline-flex items-center gap-1 text-sm tabular-nums text-muted-foreground">
          <UsersIcon className="h-3 w-3 shrink-0" />
          {r.member_count ?? 0}
        </span>
      ) },
    { key: 'actions', label: '', noFilter: true, getValue: () => '',
      render: () => <ChevronRight className="h-4 w-4 text-muted-foreground" />,
      align: 'right' },
  ], [capabilityOptions, accessOptions]);

  // Column visibility
  const defaultVisibility = useMemo<Record<string, boolean>>(() => {
    const d: Record<string, boolean> = {};
    for (const c of COLUMNS) d[c.key] = true;
    return d;
  }, [COLUMNS]);

  const [columnVisibility, setColumnVisibility] = useState<Record<string, boolean>>(() => {
    try {
      const stored = localStorage.getItem(VISIBILITY_KEY);
      return stored ? { ...defaultVisibility, ...JSON.parse(stored) } : defaultVisibility;
    } catch { return defaultVisibility; }
  });
  useEffect(() => { try { localStorage.setItem(VISIBILITY_KEY, JSON.stringify(columnVisibility)); } catch { /* storage is best-effort; ignore (private mode / quota) */ } }, [columnVisibility]);
  const isVisible = (k: string) => columnVisibility[k] !== false;
  const toggleColumn = (k: string) => setColumnVisibility((v) => ({ ...v, [k]: !isVisible(k) }));
  const visibleColumns = COLUMNS.filter((c) => isVisible(c.key));

  // Column filters
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>(() => {
    try { return JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}'); } catch { return {}; }
  });
  useEffect(() => { try { localStorage.setItem(FILTERS_KEY, JSON.stringify(columnFilters)); } catch { /* storage is best-effort; ignore (private mode / quota) */ } }, [columnFilters]);
  const setColumnFilter = (k: string, v: string) => setColumnFilters((p) => ({ ...p, [k]: v }));
  const clearFilters = () => setColumnFilters({});
  const hasActiveFilters = Object.values(columnFilters).some(Boolean);

  // Sort
  const [sortKey, setSortKey] = useState<string>('label');
  const [sortDirection, setSortDirection] = useState<1 | -1>(1);
  const handleSort = (k: string) => {
    if (k === sortKey) setSortDirection((d) => (d === 1 ? -1 : 1));
    else { setSortKey(k); setSortDirection(1); }
  };

  // Filter + sort
  const filtered = useMemo(() => {
    const active = Object.entries(columnFilters).filter(([, v]) => v);
    if (active.length === 0) return roles;
    return roles.filter((r) =>
      active.every(([key, value]) => {
        const col = COLUMNS.find((c) => c.key === key);
        if (!col || col.noFilter) return true;
        if (col.filterType === 'select') {
          let selected: string[] = [];
          try { selected = JSON.parse(value) as string[]; } catch { return true; }
          if (selected.length === 0) return true;
          const values = col.filterValues ? col.filterValues(r) : [String(col.getValue(r))];
          return values.some((v) => selected.includes(v));
        }
        return String(col.getValue(r)).toLowerCase().includes(value.toLowerCase());
      }),
    );
  }, [roles, columnFilters, COLUMNS]);

  const sorted = useMemo(() => {
    const col = COLUMNS.find((c) => c.sortKey === sortKey);
    if (!col) return filtered;
    return [...filtered].sort((a, b) => {
      const av = col.getValue(a); const bv = col.getValue(b);
      if (typeof av === 'number' && typeof bv === 'number') return sortDirection * (av - bv);
      return sortDirection * String(av).localeCompare(String(bv));
    });
  }, [filtered, sortKey, sortDirection, COLUMNS]);

  // Export
  function escapeCSV(v: unknown): string {
    if (v == null) return '';
    let s = String(v);
    if (/^[=+\-@\t\r]/.test(s)) s = "'" + s;
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  }
  const exportCols = COLUMNS.filter((c) => !c.noFilter);
  const handleCopy = () => {
    const header = exportCols.map((c) => c.label).join('\t');
    const body = sorted.map((r) => exportCols.map((c) => String(c.getValue(r))).join('\t'));
    navigator.clipboard.writeText([header, ...body].join('\n'));
    toast.success('Copied to clipboard');
  };
  const handleCSV = () => {
    const header = exportCols.map((c) => escapeCSV(c.label)).join(',');
    const body = sorted.map((r) => exportCols.map((c) => escapeCSV(c.getValue(r))).join(','));
    const blob = new Blob([[header, ...body].join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `roles_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success(`Exported ${sorted.length} rows`);
  };
  const handleRefresh = () => qc.invalidateQueries({ queryKey: roleDefinitionKeys.all });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <span className="text-sm text-muted-foreground">
          {filtered.length < roles.length
            ? `Showing ${filtered.length} of ${roles.length}`
            : `${roles.length} role${roles.length !== 1 ? 's' : ''}`}
        </span>
        <div className="flex items-center gap-2 flex-wrap">
          {hasActiveFilters && (
            <Button variant="outline" size="sm" onClick={clearFilters}>
              <FilterX className="h-4 w-4 mr-1" /> Clear filters
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={handleCopy}>
            <Copy className="h-4 w-4 mr-1" /> Copy
          </Button>
          <Button variant="outline" size="sm" onClick={handleCSV}>
            <Download className="h-4 w-4 mr-1" /> CSV
          </Button>
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={isFetching}>
            <RefreshCw className={cn('h-4 w-4 mr-1', isFetching && 'animate-spin')} /> Refresh
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm">
                <Settings2 className="h-4 w-4 mr-1" /> Columns
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Toggle columns</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {COLUMNS.filter((c) => !c.noFilter).map((c) => (
                <DropdownMenuCheckboxItem
                  key={c.key} checked={isVisible(c.key)} onCheckedChange={() => toggleColumn(c.key)}
                >
                  {c.label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden bg-white">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/30 hover:bg-muted/30">
                {visibleColumns.map((col) => (
                  <TableHead key={`f-${col.key}`} className="p-1">
                    {col.noFilter ? null : col.filterType === 'select' ? (
                      <SelectFilter
                        colKey={col.key}
                        options={col.filterOptions ?? []}
                        value={columnFilters[col.key] || ''}
                        onChange={setColumnFilter}
                      />
                    ) : (
                      <Input
                        className="h-7 text-xs"
                        value={columnFilters[col.key] || ''}
                        onChange={(e) => setColumnFilter(col.key, e.target.value)}
                        placeholder="…"
                      />
                    )}
                  </TableHead>
                ))}
              </TableRow>
              <TableRow>
                {visibleColumns.map((col) => (
                  <TableHead key={col.key} className={cn(col.align === 'right' && 'text-right')}>
                    {col.sortKey ? (
                      <Button
                        variant="ghost" size="sm"
                        className="-ml-3 h-8 hover:bg-transparent"
                        onClick={() => handleSort(col.sortKey as string)}
                      >
                        {col.label}
                        {sortKey === col.sortKey
                          ? (sortDirection === 1
                            ? <ArrowUp className="ml-1 h-3 w-3" />
                            : <ArrowDown className="ml-1 h-3 w-3" />)
                          : <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />}
                      </Button>
                    ) : col.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={`s-${i}`}>
                    {visibleColumns.map((c) => (
                      <TableCell key={c.key}><Skeleton className="h-4 w-full" /></TableCell>
                    ))}
                  </TableRow>
                ))
              ) : sorted.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleColumns.length} className="py-12 text-center text-sm text-muted-foreground">
                    No roles match these filters.
                  </TableCell>
                </TableRow>
              ) : (
                sorted.map((role) => (
                  <TableRow
                    key={role.role}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onRowClick(role)}
                  >
                    {visibleColumns.map((c) => (
                      <TableCell
                        key={c.key}
                        className={cn('text-sm', c.align === 'right' && 'text-right tabular-nums')}
                      >
                        {c.render ? c.render(role) : (String(c.getValue(role)) || '—')}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

// Multi-select dropdown for filter row (same shape as SeatsTable's)
function SelectFilter({
  colKey, options, value, onChange,
}: {
  colKey: string;
  options: { label: string; value: string }[];
  value: string;
  onChange: (key: string, value: string) => void;
}) {
  const selected: string[] = useMemo(() => {
    try { return value ? JSON.parse(value) : []; } catch { return []; }
  }, [value]);
  const toggle = (opt: string) => {
    const next = selected.includes(opt)
      ? selected.filter((o) => o !== opt)
      : [...selected, opt];
    onChange(colKey, next.length ? JSON.stringify(next) : '');
  };
  const label = selected.length === 0
    ? '—'
    : selected.length === 1
      ? (options.find((o) => o.value === selected[0])?.label ?? '1 selected')
      : `${selected.length} selected`;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 w-full text-xs justify-between font-normal">
          <span className="truncate">{label}</span>
          {selected.length > 0 && (
            <FilterX
              className="h-3 w-3 opacity-50 hover:opacity-100 shrink-0 ml-1"
              onClick={(e) => { e.stopPropagation(); onChange(colKey, ''); }}
            />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
        {options.length === 0 ? (
          <div className="p-2 text-xs text-muted-foreground">No options</div>
        ) : options.map((opt) => (
          <DropdownMenuCheckboxItem
            key={opt.value}
            checked={selected.includes(opt.value)}
            onCheckedChange={() => toggle(opt.value)}
            onSelect={(e) => e.preventDefault()}
          >
            {opt.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default RolesTable;
